import React, { useMemo, useRef, useState, useEffect } from 'react'
import {
  Paper,
  makeStyles,
  Table,
  TableRow,
  TableCell,
  TableBody,
  Tooltip,
} from '@material-ui/core'
import { useHistory } from 'react-router-dom'
import countryData from '../../data/countryData.json'
import FilterBoxCompanyPage from '../FilterBoxCompanyPage'
import { INIT_FILTER_STATE } from '../../ultils/dataUtil'

const useStyles = makeStyles({
  title: {
    fontWeight: 700,
    fontSize: 15,
    color: '#1B3963',
    borderBottom: '2px solid #1B3963',
    paddingBottom: 2,
    letterSpacing: 0.2,
    textTransform: 'capitalize',
    marginTop: 10,
  },
  filterBox: {
    display: 'flex',
    flexWrap: 'nowrap',
    overflow: 'hidden',
    gap: 8,
    margin: '8px 0 12px',
  },
  chip: { height: 24, fontSize: 12 },
  paper: {
    width: '100%',
    maxHeight: '420px',
    overflowY: 'auto',
    border: '1px solid #0000001A',
    borderRadius: 4,
    padding: 8,
    boxSizing: 'border-box',
    scrollbarWidth: 'thin',
    scrollbarColor: '#C1CFDF transparent',
    '&::-webkit-scrollbar-thumb': {
      background: '#C1CFDF',
      borderRadius: 999,
    },
    '&::-webkit-scrollbar': {
      width: 6,
    },
    '&::-webkit-scrollbar-track': {
      background: 'transparent',
    },
    '&::-webkit-scrollbar-thumb:hover': {
      background: '#A9BBD1',
    },
  },
  listHeader: {
    fontFamily: 'Inter',
    fontWeight: 500,
    fontSize: 12,
    lineHeight: '18px',
    color: '#8C8C8C',
    marginBottom: 6,
  },
  bodyCell: {
    fontStyle: 'Semi Bold',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    cursor: 'pointer',
    padding: 8,
    fontFamily: 'Inter',
    fontWeight: 600,
    fontSize: 12,
    lineHeight: '18px',
    color: '#1B3963',
    borderBottom: 'none',
    '&:hover': { background: '#F3F7FC' },
  },
  bodyActive: {
    background: '#F5F5F5',
    fontWeight: 600,
    height: 34,
    borderRadius: '3px',
    padding: '8px',
    gap: '8px',
  },
  reset: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    background: 'transparent',
    marginLeft: 'auto',
  },
  resetText: {
    fontFamily: 'Inter, sans-serif',
    fontWeight: 400,
    fontSize: 14,
    lineHeight: '22px',
    color: '#1B3963',
  },
  filter: {
    display: 'flex',
    alignItems: 'center',
    padding: '10px 10px 10px 0',
    '& > span': {
      color: '#474747',
      fontWeight: 600,
      fontSize: 14,
    },
  },
  filterBy: {
    fontSize: 14,
    lineHeight: '22px',
    fontWeight: 600,
    color: '#474747',
  },
  dropdownType: {
    width: '43%',
    background: '#F4F5F7',
    borderRadius: 3,
  },
  dropdownLocation: {
    marginLeft: 8,
    width: '43%',
    position: 'relative',
    background: '#F4F5F7',
    borderRadius: 3,
  },
})

type Ticker = {
  ticker: string
  proper_name: string
  iso_country?: string
  industry?: string
}

export const INIT_FILTER = {
  sector: [],
  industry: [],
  region: [],
  country: [],
}

type Props = {
  data: Ticker[]
  selectedFilter: typeof INIT_FILTER
  setSelectedFilter: (params: typeof INIT_FILTER) => void
  onResetAll: () => void
  tickerId: string
  activeNode: string
}

export default function CompanySidebarList({
  data,
  selectedFilter,
  setSelectedFilter,
  onResetAll,
  tickerId,
  activeNode,
}: Props) {
  const classes = useStyles()
  const history = useHistory()
  const listRef = useRef<HTMLDivElement>(null)
  const [reset, setReset] = useState(false)
  const [initFilterTitle, setInitFilterTitle] = useState(INIT_FILTER_STATE)

  const getDynamicFilterData = (staticList: any, dynamicList: any) => {
    dynamicList.forEach((item: any) => {
      staticList.forEach((innerItem: any) => {
        if (item === innerItem.value) {
          innerItem.isChecked = true
        }
      })
    })
    return staticList
  }

  const uniqueSector: any = [
    ...new Set(data?.map((t: any) => t.factset_sector_desc)),
  ]
  const sectorData = [...new Set(uniqueSector)].map((value: any, id: any) => ({
    id,
    value,
    isChecked: false,
    type: 'sector',
  }))

  const uniqueIndustry: any = [...new Set(data?.map((t: any) => t.industry))]
  const industryData = [...new Set(uniqueIndustry)].map(
    (value: any, id: any) => ({
      id,
      value,
      isChecked: false,
      type: 'industry',
    }),
  )

  const uniqueCountry: any = [...new Set(data?.map((t: any) => t.iso_country))]
  const countryDataNews = countryData
    .filter(item => uniqueCountry.includes(item.id))
    .map((value, id) => ({
      id: value.id,
      value: value.value,
    }))

  const uniqueRegion: any = [...new Set(data?.map((t: any) => t.region))]
  const regionData = [...new Set(uniqueRegion)].map((value: any, id: any) => ({
    id,
    value,
    isChecked: false,
  }))

  const internalSectorData = useMemo(() => {
    return getDynamicFilterData(sectorData, selectedFilter.sector)
  }, [data])

  const internalIndustryData = useMemo(() => {
    return getDynamicFilterData(industryData, selectedFilter.industry)
  }, [data])

  const internalRegionData = useMemo(() => {
    return getDynamicFilterData(regionData, selectedFilter.region)
  }, [data])

  const internalCountryData = useMemo(() => {
    return getDynamicFilterData(countryDataNews, selectedFilter.country)
  }, [data])

  const companyRows = useMemo(
    () =>
      [...data]
        .sort((a, b) => a.proper_name.localeCompare(b.proper_name))
        .map(r => ({ name: r.proper_name, ticker: r.ticker })),
    [data],
  )

  const setFilterValue = (val: any, type: any) => {
    if (type === 'sector') {
      const sectorList = val
        .filter((item: any) => item.type === 'sector')
        .map((item: any) => item.value)
      const industryList = val
        .filter((item: any) => item.type === 'industry')
        .map((item: any) => item.value)
      setSelectedFilter({
        ...selectedFilter,
        sector: sectorList,
        industry: industryList,
      })
    } else if (type === 'country') {
      const countryList = val
        .filter((item: any) => isNaN(item.id))
        .map((item: any) => item.id)
      const regionList = val
        .filter((item: any) => !isNaN(item.id))
        .map((item: any) => item.value)
      setSelectedFilter({
        ...selectedFilter,
        region: regionList,
        country: countryList,
      })
    }
    setReset(false)
  }

  const handleReset = () => {
    setReset(true)
    setInitFilterTitle(INIT_FILTER_STATE)
    setSelectedFilter({
      sector: [],
      industry: [],
      region: [],
      country: [],
    })
    onResetAll()
  }

  useEffect(() => {
    setReset(true)
    setInitFilterTitle(INIT_FILTER_STATE)
    setSelectedFilter({
      sector: [],
      industry: [],
      region: [],
      country: [],
    })
  }, [activeNode])

  return (
    <>
      <div className={classes.filter}>
        <div className={classes.dropdownType}>
          <FilterBoxCompanyPage
            placeholder={initFilterTitle.sector}
            data={[
              {
                title: 'Sector',
                list: internalSectorData,
                heightScrollList: 160,
              },
              {
                title: 'Industry',
                list: internalIndustryData,
                heightScrollList: 160,
              },
            ]}
            setFilterValue={(val: any) => setFilterValue(val, 'sector')}
            isSearch={false}
            isRadio={false}
            isReset={reset}
            customStyle={{ width: '100%' }}
            customStyleBody={{ width: 'calc(200% + 8px)' }}
            itemInfo={[
              sectorData.map((itemOut: any) => {
                return {
                  id: itemOut.id,
                  value: data.filter(
                    (item: any) => item.factset_sector_desc === itemOut.value,
                  ).length,
                  isChecked: reset || itemOut.isChecked,
                  type: 'sector',
                }
              }),
              industryData.map((itemOut: any) => {
                return {
                  id: itemOut.id,
                  value: data.filter(
                    (item: any) => item.industry === itemOut.value,
                  )?.length,
                  isChecked: reset || itemOut.isChecked,
                  type: 'industry',
                }
              }),
            ]}
          />
        </div>
        <div className={classes.dropdownLocation}>
          <FilterBoxCompanyPage
            placeholder={initFilterTitle.country}
            data={[
              {
                title: 'Region',
                list: internalRegionData,
                heightScrollList: 160,
              },
              {
                title: 'Country',
                list: internalCountryData,
                heightScrollList: 160,
              },
            ]}
            setFilterValue={(val: any) => {
              setFilterValue(val, 'country')
            }}
            isSearch
            isRadio={false}
            isReset={reset}
            customStyle={{ width: '100%' }}
            customStyleBody={{
              width: 'calc(200% + 8px)',
              left: '-100%',
              position: 'absolute',
            }}
            itemInfo={[
              regionData.map((itemOut: any) => {
                return {
                  id: itemOut.id,
                  value: data.filter(
                    (item: any) => item.region === itemOut.value,
                  ).length,
                  isChecked: reset || itemOut.isChecked,
                }
              }),
              countryDataNews.map((itemOut: any) => {
                return {
                  id: itemOut.id,
                  value: data.filter(
                    (item: any) => item.iso_country === itemOut.id,
                  ).length,
                  isChecked: reset || itemOut.isChecked,
                }
              }),
            ]}
          />
        </div>

        <span onClick={handleReset} className={classes.reset}>
          <span className={classes.resetText}>Reset</span>
        </span>
      </div>
      {/* -------- Company list ---------- */}
      <div className={classes.listHeader}>Company Name</div>
      <Paper elevation={0} className={classes.paper} ref={listRef}>
        <Table
          size="small"
          style={{
            tableLayout: 'fixed',
            width: '100%',
            zIndex: 1,
            borderCollapse: 'separate',
            borderSpacing: '0 4px',
          }}
        >
          <TableBody>
            {companyRows.map(r => (
              <TableRow key={r.ticker}>
                <TableCell
                  className={`${classes.bodyCell} ${
                    r.ticker === tickerId ? classes.bodyActive : ''
                  }`}
                  onClick={() => {
                    history.push(`/company/${r.ticker}`, { fromChart: true })
                  }}
                >
                  <Tooltip title={r.name} arrow>
                    <span>{r.name}</span>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    </>
  )
}
